/**
 * Smooth scrolling (ScrollSmoother) and programmatic scrolls.
 *
 * @module lib/scroll
 */

import { REDUCED_MOTION } from '../config.js';
import { ScrollSmoother, ScrollTrigger, gsap } from './gsap.js';

/** @type {ScrollSmoother | null} */
let smoother = null;

/**
 * Starts smooth scrolling, except where it measures wrong or is unwanted.
 */
export function createSmoother() {
    if (REDUCED_MOTION || !document.getElementById('smooth-wrapper')) {
        return;
    }

    // Touch-only devices: with smoothTouch: 0 the smoother switches itself
    // off (native scroll, no transform), yet a ScrollTrigger refresh made
    // while the page is scrolled then adds the scroll offset to every
    // trigger — reveals fire late or never, strips scroll empty cards.
    // Native scrolling without the smoother measures right.
    if (ScrollTrigger.isTouch === 1) {
        return;
    }

    smoother = ScrollSmoother.create({
        wrapper: '#smooth-wrapper',
        content: '#smooth-content',
        smooth: 1.1,
        smoothTouch: 0,
        normalizeScroll: false,
        ignoreMobileResize: true,
    });
}

/** @returns {ScrollSmoother | null} The smoother, when the page has one. */
export function getSmoother() {
    return smoother;
}

/**
 * Scrolls the page to a target, eased.
 *
 * @param {Element | number} target
 * @param {string} [position] - Where the target lands on screen ('center center'); top by default.
 */
export function scrollToTarget(target, position) {
    if (smoother) {
        smoother.scrollTo(target, true, position);
        return;
    }

    let offset = 0;

    if (position && target instanceof Element) {
        offset = -(window.innerHeight - target.getBoundingClientRect().height) / 2;
    }

    gsap.to(window, { scrollTo: { y: target, offsetY: -offset }, duration: 1.1, ease: 'power3.inOut' });
}

/**
 * Sections are rendered by script, so the browser cannot honour an incoming
 * #hash on its own: replay it once the markup exists.
 */
export function jumpToHash() {
    const { hash } = window.location;

    if (!hash || hash.length < 2) {
        return;
    }

    let target;

    try {
        target = document.querySelector(hash);
    } catch {
        return;
    }

    if (!target) {
        return;
    }

    if (smoother) {
        smoother.scrollTo(target, false, 'top 40px');
    } else {
        target.scrollIntoView();
    }

    ScrollTrigger.refresh();
}
